<template>
  <div class="space-y-4">
    <!-- Page header -->
    <div class="flex items-center gap-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">translate</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Localizations</h2>
        <p class="text-sm text-gray-500">Manage in-app text for each supported language.</p>
      </div>
    </div>

    <div class="flex flex-col gap-4 lg:flex-row" style="min-height: 70vh">
      <!-- Language list -->
      <aside class="w-full lg:w-1/4 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-100">
          <span class="text-xs font-semibold tracking-widest text-gray-500 uppercase">Languages</span>
        </div>
        <div class="p-3 space-y-1 overflow-y-auto" style="max-height: 55vh">
          <button
            v-for="(localization, index) in localizations"
            :key="localization._id"
            @click="selectLocalization(index)"
            class="flex items-center w-full gap-3 px-3 py-2 text-left rounded-xl transition-colors duration-150 group"
            :class="
              selectedLocalization && localization._id === selectedLocalization._id
                ? 'bg-taxiye-50 ring-1 ring-taxiye-200'
                : 'hover:bg-gray-50'
            "
          >
            <span
              class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm font-semibold uppercase rounded-xl"
              :class="
                selectedLocalization && localization._id === selectedLocalization._id
                  ? 'bg-taxiye-500 text-white'
                  : 'bg-gray-100 text-gray-600'
              "
            >
              {{ localization.name.slice(0, 2) }}
            </span>
            <span class="flex-grow text-sm font-medium text-gray-800 capitalize truncate">{{ localization.name }}</span>
            <span
              v-if="localization.name != 'english'"
              @click.stop="deleteLocalization(localization.name)"
              class="flex items-center justify-center flex-shrink-0 w-8 h-8 text-gray-400 rounded-full opacity-0 transition-all duration-150 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500"
              title="Delete language"
            >
              <span class="text-base material-icons-round">delete_outline</span>
            </span>
          </button>
        </div>

        <div class="p-3 border-t border-gray-100">
          <button
            @click="addingLocalizations = !addingLocalizations"
            class="flex items-center justify-center w-full gap-1 px-4 h-10 text-sm font-semibold rounded-xl transition-colors duration-150"
            :class="addingLocalizations ? 'bg-gray-100 text-gray-600' : 'bg-taxiye-50 text-taxiye-600 hover:bg-taxiye-100'"
          >
            <span class="text-base material-icons-round">{{ addingLocalizations ? 'close' : 'add' }}</span>
            {{ addingLocalizations ? 'Cancel' : 'Add Localization' }}
          </button>
          <div v-if="addingLocalizations" class="mt-3 space-y-2">
            <input
              class="w-full h-10 px-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Language name"
              v-model="newLocalizationName"
              @keyup.enter="newLocalizationName.length >= 3 && addLocalization()"
            />
            <button
              class="w-full h-10 text-sm font-semibold text-white rounded-xl transition-colors duration-150"
              :class="newLocalizationName.length >= 3 ? 'bg-taxiye-500 hover:bg-taxiye-600' : 'bg-gray-300 cursor-not-allowed'"
              :disabled="newLocalizationName.length < 3"
              @click="addLocalization()"
            >
              Create
            </button>
          </div>
        </div>
      </aside>

      <!-- Detail pane -->
      <section class="flex-grow bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div v-if="selectedLocalization !== null">
          <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h3 class="text-base font-semibold text-gray-800 capitalize">{{ selectedLocalization.name }}</h3>
          </div>
          <div class="flex px-5 border-b border-gray-100">
            <button
              @click="selectTab('passenger')"
              class="relative px-4 py-3 text-sm font-semibold transition-colors duration-150"
              :class="selectedTab == 'passenger' ? 'text-taxiye-600' : 'text-gray-500 hover:text-gray-700'"
            >
              Passenger
              <span v-if="selectedTab == 'passenger'" class="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-taxiye-500"></span>
            </button>
            <button
              @click="selectTab('driver')"
              class="relative px-4 py-3 text-sm font-semibold transition-colors duration-150"
              :class="selectedTab == 'driver' ? 'text-taxiye-600' : 'text-gray-500 hover:text-gray-700'"
            >
              Driver
              <span v-if="selectedTab == 'driver'" class="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-taxiye-500"></span>
            </button>
          </div>
          <div class="overflow-y-auto" style="max-height: 60vh">
            <Driver v-if="selectedTab == 'driver'" :localization="selectedLocalization"></Driver>
            <Passenger v-if="selectedTab == 'passenger'" :localization="selectedLocalization"></Passenger>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center h-full py-24 text-center">
          <span class="text-5xl text-gray-300 material-icons-round">language</span>
          <p class="mt-3 text-sm font-medium text-gray-500">Select a language from the list to edit its localizations.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Driver from "@/views/Localization/Driver";
import Passenger from "@/views/Localization/Passenger";

export default {
  components: {
    Driver,
    Passenger
  },
  data() {
    return {
      localizations: [],
      selectedLocalization: null,
      selectedTab: "passenger",
      loading: false,
      searchKey: "",
      addingLocalizations: false,
      newLocalizationName: ""
    };
  },
  watch: {
    selectedLocalization() {
      this.selectedTab = "passenger";
    },
    searchKey() {
      this.getLocalizations();
    }
  },
  created() {
    this.getLocalizations();
  },
  methods: {
    getLocalizations() {
      this.$misc
        .get("/localization-languages")
        .then(response => {
          this.localizations = response.data
          this.selectedLocalization = this.localizations[0];
          this.selectedTab = 'passenger'
        })
        .catch(err => console.log(err));
    },
    selectLocalization(index) {
      this.selectedLocalization = this.localizations[index];
    },
    updateState() {
      this.$misc
        .patch("/localizations/" + this.selectedLocalization._id, {
          active: this.selectedLocalization.active
        })
        .then(response => console.log(response))
        .catch(err => console.log(err));
    },
    addLocalization() {
      this.$misc
        .post("/localization-languages", {
          name: this.newLocalizationName
        })
        .then(() => {
          this.getLocalizations();
        })
        .catch(err => alert(JSON.stringify(err.response.data)));
    },
    deleteLocalization(name) {
      var r = confirm("Are you sure you want to delete this localization?");
      if (r == true) {
        Promise.all([
          this.$misc
            .delete(`/passenger-localizations/${name}`),

          this.$misc
            .delete(`/driver-localizations/${name}`)
        ])
          .catch(() => alert("something went wrong while deleting the localization"))
          .finally(() => {
            this.getLocalizations();
          })
      }
    },
    selectTab(tab) {
      this.selectedTab = tab;
    }
  }
};
</script>

<style scoped>
/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 25px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 25px;
  width: 25px;
  left: 0px;
  bottom: 0px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #2196f3;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196f3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>